import React from "react";
import { Box, Button, Divider, Stack, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  DndContext, KeyboardSensor, PointerSensor, closestCenter,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { LegacyTokens, TypeScale } from "../../../../theme/b13Tokens";
import { B13, B13Ink, tint12 } from "../../../../theme/b13Tokens";
import PromptLevelCard, { PromptLevelCardValue } from "./PromptLevelCard";
import TemplateSettingsMenu from "./TemplateSettingsMenu";
import { OutcomeState } from "./OutcomeStateToggle";
import { OutcomeMode, DEFAULT_OUTCOME_MODE } from "./outcomeModeConstants";

// Drag-reorder client key. Assigned once per level object, never
// persisted. Consumers (Editor/index.tsx, HierarchyQuickModal.tsx) send
// a whitelisted payload, so this field stays UI-side. Required for
// @dnd-kit sortable identity AND for the React key on the mapped card:
// without a stable key, reordering unmounts every card and destroys an
// in-flight edit's caret + focus.
let _dndSeq = 0;
const nextDndId = () => `L${++_dndSeq}`;

// Batch 6 — the shared body used by both the Settings page and the
// QuickHierarchyDrawer's hierarchy branch. Consumers own state and the
// SAVE handler; this body just renders the form and lifts events.

export type EditorLevel = PromptLevelCardValue;

export function makeEmptyLevel(order: number): EditorLevel {
  // Ticket §2 / §10 — user-clicked "Add prompt level" defaults to
  // CENTER (Successful outcome). "not_counted" remains a valid backend
  // enum for historical rows but is no longer the interactive default.
  return {
    levelName: "",
    colour: LegacyTokens.blue,
    outcomeState: "successful",
    shortCode: "",
    score: 50,
    description: "",
    isActive: true,
    countsAsIndependent: true,
    orderNumber: order,
    weightedScore: null,
    _dndId: nextDndId(),
  };
}

export function normaliseLevel(raw: any, i: number): EditorLevel {
  const explicit = raw?.outcomeState as OutcomeState | undefined;
  const outcomeState: OutcomeState =
    explicit === "unsuccessful" || explicit === "not_counted" || explicit === "successful"
      ? explicit
      : raw?.countsAsIndependent
        ? "successful"
        : "not_counted";
  // Batch 6A — weightedScore null-preserving parse. Never derived from
  // `score`; if the raw row didn't set weightedScore, it stays null.
  const rawWs = raw?.weightedScore;
  const weightedScore: number | null =
    rawWs === null || rawWs === undefined || rawWs === ""
      ? null
      : Number(rawWs);
  return {
    id: raw?.id,
    levelName: raw?.levelName ?? "",
    colour: raw?.colour || LegacyTokens.blue,
    outcomeState,
    shortCode: raw?.shortCode || "",
    score: Number(raw?.score) || 0,
    description: raw?.description || "",
    isActive: raw?.isActive !== false,
    countsAsIndependent: outcomeState === "successful",
    orderNumber: raw?.orderNumber ?? i,
    weightedScore: Number.isFinite(weightedScore as number) ? (weightedScore as number) : null,
    _dndId: raw?._dndId ?? nextDndId(),
  };
}

export type PromptLevelEditorBodyProps = {
  templateName: string;
  onTemplateNameChange: (v: string) => void;
  templateNameError: string | null;

  description: string;
  onDescriptionChange: (v: string) => void;
  isDefault: boolean;
  onDefaultChange: (v: boolean) => void;
  direction: string;
  onDirectionChange: (v: string) => void;
  onReverse: () => void;

  // Batch 6A — template-level outcome mode.
  outcomeMode: OutcomeMode;
  onOutcomeModeChange: (m: OutcomeMode) => void;

  levels: EditorLevel[];
  onLevelsChange: (updater: (prev: EditorLevel[]) => EditorLevel[]) => void;
  levelErrors: Record<number, string | null>;

  registerNameRef?: (el: HTMLInputElement | null) => void;
  registerLevelNameRef?: (index: number, el: HTMLInputElement | null) => void;

  readOnly?: boolean;
};

export default function PromptLevelEditorBody(props: PromptLevelEditorBodyProps) {
  const {
    templateName, onTemplateNameChange, templateNameError,
    description, onDescriptionChange, isDefault, onDefaultChange,
    direction, onDirectionChange, onReverse,
    outcomeMode, onOutcomeModeChange,
    levels, onLevelsChange, levelErrors,
    registerNameRef, registerLevelNameRef, readOnly,
  } = props;

  const patchLevel = (i: number, patch: Partial<EditorLevel>) =>
    onLevelsChange((L) => L.map((x, k) => (k === i ? { ...x, ...patch } : x)));

  const move = (from: number, to: number) => {
    if (to < 0 || to >= levels.length) return;
    onLevelsChange((L) => {
      const c = [...L];
      const [x] = c.splice(from, 1);
      c.splice(to, 0, x);
      return c.map((l, i) => ({ ...l, orderNumber: i }));
    });
  };

  // Drag-reorder sensors. PointerSensor with a small activation
  // distance so a click on the handle doesn't immediately count as a
  // drag (protects against accidental pickup when a user clicks the
  // handle by mistake). KeyboardSensor + sortableKeyboardCoordinates
  // gives Space=pick up, Arrow=move, Space=drop, Escape=cancel with no
  // hand-rolled key handling.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onLevelsChange((L) => {
      const from = L.findIndex((l) => (l._dndId ?? "") === active.id);
      const to = L.findIndex((l) => (l._dndId ?? "") === over.id);
      if (from < 0 || to < 0) return L;
      // arrayMove preserves object identity, so patchLevel's next call
      // patches the same object reference the user was editing.
      return arrayMove(L, from, to).map((l, i) => ({ ...l, orderNumber: i }));
    });
  };

  const duplicate = (i: number) =>
    onLevelsChange((L) => {
      const c = [...L];
      // Fresh _dndId so the copy is a distinct sortable — see the same
      // fix in WorkflowEditorBody.duplicate for the rationale.
      c.splice(i + 1, 0, { ...c[i], id: undefined, _dndId: nextDndId(), levelName: c[i].levelName + " (copy)" });
      return c.map((l, k) => ({ ...l, orderNumber: k }));
    });

  const remove = (i: number) =>
    onLevelsChange((L) => (L.length <= 1 ? L : L.filter((_, k) => k !== i).map((l, k) => ({ ...l, orderNumber: k }))));

  const addLevel = () =>
    onLevelsChange((L) => [...L, makeEmptyLevel(L.length)]);

  return (
    <Box>
      {/* §6.2 — Template name row + gear */}
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 3 }}>
        <TextField
          fullWidth
          size="medium"
          label="Template name"
          value={templateName}
          disabled={readOnly}
          onChange={(e) => onTemplateNameChange(e.target.value)}
          inputRef={registerNameRef}
          error={Boolean(templateNameError)}
          helperText={templateNameError || " "}
          InputLabelProps={{ shrink: true }}
          FormHelperTextProps={{ sx: { color: templateNameError ? LegacyTokens.red : LegacyTokens.ink3 } }}
          sx={templateNameError
            ? { "& .MuiOutlinedInput-notchedOutline": { borderColor: LegacyTokens.red, borderWidth: 2 } }
            : undefined}
        />
        <Box sx={{ pt: 0.5 }}>
          <TemplateSettingsMenu
            description={description}
            onDescriptionChange={onDescriptionChange}
            isDefault={isDefault}
            onDefaultChange={onDefaultChange}
            direction={direction}
            onDirectionChange={onDirectionChange}
            onReverse={onReverse}
            outcomeMode={outcomeMode}
            onOutcomeModeChange={onOutcomeModeChange}
            disabled={readOnly}
          />
        </Box>
      </Stack>

      <Typography sx={{ ...TypeScale.sectionHead, mb: 1.5 }}>Levels</Typography>

      {/* §6.3 — level cards. Sortable identity keyed on _dndId so a
           reorder never remounts a card (an in-flight edit's caret and
           focus survive). Chevrons are preserved alongside the drag
           handle: keyboard-only users may prefer discrete click targets
           to arrow-key nudging, and if the KeyboardSensor ever misfires
           the chevrons remain a working keyboard path. */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={levels.map((l) => l._dndId ?? "")}
          strategy={verticalListSortingStrategy}
        >
          <Stack spacing={1.5}>
            {levels.map((lv, i) => (
              <PromptLevelCard
                key={lv._dndId ?? `${lv.id ?? "new"}-${i}`}
                sortableId={lv._dndId}
                index={i}
                total={levels.length}
                value={lv}
                nameError={levelErrors[i] ?? null}
                outcomeMode={outcomeMode}
                registerNameRef={(el) => registerLevelNameRef?.(i, el)}
                onChange={(patch) => patchLevel(i, patch)}
                onMoveUp={() => move(i, i - 1)}
                onMoveDown={() => move(i, i + 1)}
                onDuplicate={() => duplicate(i)}
                onDelete={() => remove(i)}
                readOnly={readOnly}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>

      {/* §6.5 — add level */}
      {!readOnly && (
        <Box sx={{ mt: 2 }}>
          <Button
            fullWidth
            startIcon={<AddIcon />}
            onClick={addLevel}
            sx={{
              py: 1.25,
              border: `1.5px dashed ${LegacyTokens.line}`,
              color: B13.primary,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              fontWeight: 700,
              backgroundColor: "transparent",
              "&:hover": {
                borderColor: B13.primary,
                backgroundColor: tint12(B13.primary),
              },
            }}
          >
            Add prompt level
          </Button>
        </Box>
      )}
    </Box>
  );
}
